import { createTheme, alpha } from '@mui/material/styles';

// ─── WSE Hello Design System – Brand Tokens ──────────────────────────────────
// Colors: https://hello.onewse.com/?path=/docs/tokens-colors--code
// Typography: https://hello.onewse.com/?path=/docs/tokens-typography--code

export const WSE_COLORS = {
  primary: {
    lighter: '#CDDEFF',
    light: '#6DAAFB',
    medium: '#136DD3',
    main: '#003359',
    dark: '#0C2541',
    darker: '#000000',
    contrastText: '#FFFFFF',
  },
  // Primary-B is used as the accent / error colour
  primaryB: {
    lighter: '#FFD2CD',
    light: '#FC7F79',
    main: '#DE1135',
    dark: '#BB032A',
    darker: '#760914',
    contrastText: '#FFFFFF',
  },
  secondary: {
    lighter: '#B9EEFB',
    light: '#64CFE9',
    main: '#098198',
    dark: '#045969',
    darker: '#023742',
    contrastText: '#FFFFFF',
  },
  info: {
    lighter: '#CDDEFF',
    light: '#6DAAFB',
    main: '#0A64C9',
    dark: '#0C3A84',
    darker: '#0C2541',
    contrastText: '#FFFFFF',
  },
  success: {
    lighter: '#D3EFDA',
    light: '#7FD99A',
    main: '#0E8345',
    dark: '#0D572D',
    darker: '#002F14',
    contrastText: '#FFFFFF',
  },
  warning: {
    lighter: '#FFF5CC',
    light: '#FFD666',
    main: '#FFAB00',
    dark: '#B76E00',
    darker: '#7A4100',
    contrastText: '#1A202C',
  },
  error: {
    lighter: '#FFD2CD',
    light: '#FC7F79',
    main: '#DE1135',
    dark: '#BB032A',
    darker: '#760914',
    contrastText: '#FFFFFF',
  },
  grey: {
    100: '#F7FAFC',
    200: '#EDF2F7',
    300: '#E2E8F0',
    400: '#CBD5E0',
    500: '#A0AEC0',
    600: '#718096',
    700: '#4A5568',
    800: '#2D3748',
    900: '#1A202C',
  },
} as const;

export function createAppTheme(mode: 'light' | 'dark') {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        light: WSE_COLORS.primary.light,
        main: WSE_COLORS.primary.main,
        dark: WSE_COLORS.primary.dark,
        contrastText: WSE_COLORS.primary.contrastText,
      },
      secondary: {
        light: WSE_COLORS.secondary.light,
        main: WSE_COLORS.secondary.main,
        dark: WSE_COLORS.secondary.dark,
        contrastText: WSE_COLORS.secondary.contrastText,
      },
      error: {
        light: WSE_COLORS.error.light,
        main: WSE_COLORS.error.main,
        dark: WSE_COLORS.error.dark,
        contrastText: WSE_COLORS.error.contrastText,
      },
      info: {
        light: WSE_COLORS.info.light,
        main: WSE_COLORS.info.main,
        dark: WSE_COLORS.info.dark,
        contrastText: WSE_COLORS.info.contrastText,
      },
      success: {
        light: WSE_COLORS.success.light,
        main: WSE_COLORS.success.main,
        dark: WSE_COLORS.success.dark,
        contrastText: WSE_COLORS.success.contrastText,
      },
      warning: {
        light: WSE_COLORS.warning.light,
        main: WSE_COLORS.warning.main,
        dark: WSE_COLORS.warning.dark,
        contrastText: WSE_COLORS.warning.contrastText,
      },
      background: {
        default: isDark ? '#07101E' : '#F7FAFC',
        paper:   isDark ? '#0E1C2F' : '#FFFFFF',
      },
      text: {
        primary:   isDark ? '#EDF2F7' : '#1A202C',
        secondary: isDark ? '#94A3B8' : '#718096',
        disabled:  isDark ? '#64748B' : '#A0AEC0',
      },
      divider: isDark
        ? alpha('#FFFFFF', 0.08)
        : alpha('#1A202C',  0.10),
      grey: WSE_COLORS.grey,
    },

    // ── Typography ──────────────────────────────────────────────────────────
    // Font: Noto Sans — sizes/weights from the HDS token sheet
    typography: {
      fontFamily: '"Noto Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif',
      h1: { fontSize: '4rem',     lineHeight: '5rem',     fontWeight: 800, letterSpacing: 0 },
      h2: { fontSize: '3rem',     lineHeight: '4rem',     fontWeight: 800, letterSpacing: 0 },
      h3: { fontSize: '2rem',     lineHeight: '3rem',     fontWeight: 700, letterSpacing: 0 },
      h4: { fontSize: '1.5rem',   lineHeight: '2.25rem',  fontWeight: 700, letterSpacing: 0 },
      h5: { fontSize: '1.25rem',  lineHeight: '1.875rem', fontWeight: 700, letterSpacing: 0 },
      h6: { fontSize: '1.125rem', lineHeight: '1.75rem',  fontWeight: 700, letterSpacing: 0 },
      subtitle1: { fontSize: '1rem',     lineHeight: '1.5rem',   fontWeight: 600, letterSpacing: 0 },
      subtitle2: { fontSize: '0.875rem', lineHeight: '1.375rem', fontWeight: 600, letterSpacing: 0 },
      body1:     { fontSize: '1rem',     lineHeight: '1.5rem',   fontWeight: 400, letterSpacing: 0 },
      body2:     { fontSize: '0.875rem', lineHeight: '1.375rem', fontWeight: 400, letterSpacing: 0 },
      caption:   { fontSize: '0.75rem',  lineHeight: '1.125rem', fontWeight: 400, letterSpacing: 0 },
      overline:  { fontSize: '0.75rem',  lineHeight: '1.125rem', fontWeight: 700, letterSpacing: 0, textTransform: 'uppercase' },
      button:    { fontSize: '0.875rem', lineHeight: '1.5rem',   fontWeight: 700, letterSpacing: 0, textTransform: 'none' },
    },

    shape: {
      borderRadius: 10,
    },

    // ── Component overrides ─────────────────────────────────────────────────
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: 'none',
            fontWeight: 700,
          },
          containedPrimary: {
            backgroundColor: '#003359',
            '&:hover': { backgroundColor: '#0C2541' },
          },
          containedSecondary: {
            backgroundColor: '#098198',
            '&:hover': { backgroundColor: '#045969' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isDark
              ? '0 1px 3px rgba(0,0,0,0.4)'
              : '0 1px 3px rgba(0,0,0,0.08),0 1px 2px rgba(0,0,0,0.06)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 600 },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            backgroundColor: isDark ? '#0E1C2F' : '#003359',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            ...(isDark && { backgroundColor: '#0E1C2F' }),
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            textTransform: 'none',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: { fontWeight: 700 },
        },
      },
    },
  });
}

export default createAppTheme('light');
